// src/Components/AdminTicketsPage.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  List,
  Paper,
  IconButton,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  CheckCircle,
  HourglassEmpty,
  AssignmentTurnedIn,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiFetch from '../utils/api';

function AdminTicketsPage() {
  const { user } = useAuth();

  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());

      const response = await apiFetch(`/api/admin/tickets?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }

      const data = await response.json();
      setTickets(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      setSubmitting(true);
      const response = await apiFetch(`/api/admin/tickets/${selectedTicket._id}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyMessage }),
      });

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      const updatedTicket = await response.json();
      setSelectedTicket(updatedTicket);
      setTickets(tickets.map(t => t._id === updatedTicket._id ? updatedTicket : t));
      setReplyMessage('');
      setError(null);
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await apiFetch(`/api/admin/tickets/${selectedTicket._id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const updatedTicket = await response.json();
      setSelectedTicket(updatedTicket);
      setTickets(tickets.map(t => t._id === updatedTicket._id ? updatedTicket : t));
      setError(null);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update ticket status');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      open: { label: 'Open', icon: <HourglassEmpty />, color: 'primary' },
      pending: { label: 'Pending', icon: <HourglassEmpty />, color: 'warning' },
      resolved: { label: 'Resolved', icon: <CheckCircle />, color: 'success' },
      closed: { label: 'Closed', icon: <AssignmentTurnedIn />, color: 'default' },
    };

    const config = statusConfig[status] || statusConfig.open;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (loading && tickets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={() => setSelectedTicket(null)} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            {selectedTicket.subject}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedTicket.status}
              label="Status"
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="caption" color="textSecondary">
              Submitted by <strong>{selectedTicket.owner?.username || 'Unknown'}</strong>
              {' \u2022 '}
              Created {formatDistanceToNow(new Date(selectedTicket.createdAt), { addSuffix: true })}
            </Typography>
          </CardContent>
        </Card>

        {/* Messages */}
        <List sx={{ mb: 2 }}>
          {selectedTicket.messages.map((msg, index) => (
            <React.Fragment key={msg._id || index}>
              <Paper
                elevation={msg.authorType === 'admin' ? 2 : 0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: msg.authorType === 'admin' ? 'action.hover' : 'background.paper',
                  borderLeft: msg.authorType === 'admin' ? '4px solid' : 'none',
                  borderColor: 'primary.main',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {msg.authorType === 'admin' ? 'Support Team' : msg.author?.username || 'User'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              </Paper>
            </React.Fragment>
          ))}
        </List>

        {/* Reply Box */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Admin Reply</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Type your reply..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              disabled={submitting}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              onClick={handleReply}
              disabled={submitting || !replyMessage.trim()}
            >
              Send Reply
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Ticket List View
  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
          Support Tickets
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          View and respond to user support tickets.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
      </Box>

      {tickets.length === 0 ? (
        <Paper sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="textSecondary">
            No tickets found
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {tickets.map((ticket) => (
            <Grid item xs={12} key={ticket._id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                }}
                onClick={() => setSelectedTicket(ticket)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">{ticket.subject}</Typography>
                    {getStatusChip(ticket.status)}
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Submitted by <strong>{ticket.owner?.username || 'Unknown'}</strong>
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      {ticket.messages.length} {ticket.messages.length === 1 ? 'message' : 'messages'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Updated {formatDistanceToNow(new Date(ticket.updatedAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default AdminTicketsPage;
